/**
 * Invoice IN stock entry + lot/warehouse reconcile (request 0111).
 *
 * Fiscal note is the stock-entry fallback when there is no goods receipt.
 * OUT never posts. Cancel reuses goods-receipt F1 conditional unpost.
 */
import { rtLogService } from '@rammetec/core';
import { repositoryResolver } from '@rammetec/data-access';
import { formatEntityLabel } from '../display/entityLabelFormat.js';
import { INVOICE } from '../types/invoice.js';
import { INVOICE_ITEM } from '../types/invoice-item.js';
import { PRODUCT } from '../types/product.js';
import { STOCK_LOCATION } from '../types/stock-location.js';
import { canInvoiceTransition, invoiceService } from './invoiceService.js';
import { invoiceReceivingService } from './invoiceReceivingService.js';
import { ChildCrudService } from './ChildCrudService.js';
import { resolveProductStockControl } from './productStockControl.js';
import { warehouseMapService } from './warehouseMapService.js';
import { lotService } from './lotService.js';
import { isThirdPartyCfop, parseThirdPartyCfops } from './thirdPartyCfop.js';
const SOURCE_TYPE = 'invoice';
function round2(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
/**
 * Whether the reconcile proposal can be authorized without operator review.
 * Third-party CFOP lines (0206 §2.1) may lack a resolved conciliation location —
 * authorize forces the org third-party destination.
 */
export function canAutoConfirmStockEntry(proposal, thirdPartyCfops = []) {
    if (proposal.invoice.generatesStockEntry === false)
        return false;
    const stockLines = proposal.lines.filter((l) => l.stockControlEnabled);
    if (stockLines.length === 0)
        return false;
    for (const line of proposal.lines) {
        if (line.blockers.length > 0)
            return false;
        if (line.warehouseUnmapped)
            return false;
    }
    for (const line of stockLines) {
        if (line.baseQuantity == null)
            return false;
        if (isThirdPartyCfop(line.cfop, [...thirdPartyCfops]))
            continue;
        if (!line.stockLocationId?.trim())
            return false;
    }
    return true;
}
function toMovementDateIso(d) {
    const date = d instanceof Date ? d : d ? new Date(d) : new Date();
    if (Number.isNaN(date.getTime()))
        return new Date().toISOString();
    return date.toISOString();
}
class InvoiceStockService {
    getInvoiceRepo(orgId) {
        return repositoryResolver.resolveWithContext(INVOICE, { orgId });
    }
    async reversePreEntrySources(stock, invoiceId) {
        for (const sourceType of InvoiceStockService.PRE_ENTRY_REVERSE_SOURCE_TYPES) {
            const movs = await stock.listBySource(sourceType, invoiceId);
            const ids = movs
                .filter((m) => !m.reversedMovementId)
                .map((m) => m.id);
            if (ids.length === 0)
                continue;
            await stock.reverseUnpostMany(ids, `${sourceType}-reverse:${invoiceId}`, 'Cancelamento da nota fiscal');
        }
    }
    getItemRepo(orgId, invoiceId) {
        return repositoryResolver.resolveWithContext(INVOICE_ITEM, {
            orgId,
            invoiceId,
        });
    }
    async itemsCrud(orgId, invoiceId) {
        return new ChildCrudService(await this.getItemRepo(orgId, invoiceId), {
            entity: INVOICE_ITEM,
        });
    }
    /**
     * Propose lot/location resolution for an IN draft (user reviews before authorize).
     */
    async proposeReconcile(orgId, invoiceId, opts) {
        const invoice = await this.getInvoiceRepo(orgId).then((r) => r.getById(invoiceId));
        if (!invoice)
            throw new Error('Nota fiscal não encontrada.');
        if (invoice.direction !== 'IN') {
            throw new Error('Só notas de entrada podem gerar entrada de estoque.');
        }
        const items = await this.getItemRepo(orgId, invoiceId).then((r) => r.getAll());
        const locations = await repositoryResolver
            .resolveWithContext(STOCK_LOCATION, { orgId })
            .then((r) => r.getAll());
        const defaultId = opts.defaultStockLocationId?.trim() || null;
        const lines = [];
        for (const item of items) {
            const warnings = [];
            const blockers = [];
            if (!item.productId?.trim()) {
                blockers.push('Produto não resolvido.');
            }
            let product = null;
            if (item.productId) {
                try {
                    product =
                        (await repositoryResolver
                            .resolveWithContext(PRODUCT, { orgId })
                            .then((r) => r.getById(item.productId))) ?? null;
                }
                catch {
                    product = null;
                }
            }
            const control = product
                ? resolveProductStockControl(product, opts.orgStockParams).values
                : {
                    stockControlEnabled: false,
                    stockControlByLot: false,
                    stockHasExpiry: false,
                    stockControlByVolume: false,
                };
            const lotRequired = control.stockControlByLot;
            const lotText = item.lot?.trim() || null;
            let lotId = null;
            if (lotRequired) {
                if (!lotText) {
                    blockers.push('Produto controla lote — informe o número do lote.');
                }
                else if (item.productId) {
                    lotId = await this._findOrCreateLotId(orgId, item.productId, item.variantId ?? null, lotText, invoice.partyId, item.expiryDate ?? null, opts.orgStockParams ?? null);
                }
            }
            const warehouseText = item.warehouseId?.trim() || null;
            let stockLocationId = null;
            let warehouseUnmapped = false;
            if (control.stockControlEnabled) {
                if (warehouseText) {
                    const mapped = await warehouseMapService.findByCode(orgId, warehouseText);
                    if (mapped) {
                        stockLocationId = mapped.stockLocationId;
                    }
                    else {
                        const asNum = Number(warehouseText);
                        if (Number.isFinite(asNum)) {
                            const byCode = locations.find((l) => l.code === asNum && l.active !== false);
                            if (byCode)
                                stockLocationId = byCode.id;
                        }
                        if (!stockLocationId) {
                            warehouseUnmapped = true;
                            if (defaultId) {
                                stockLocationId = defaultId;
                                warnings.push(`Depósito "${warehouseText}" sem de-para — usando local padrão.`);
                            }
                            else {
                                blockers.push(`Depósito "${warehouseText}" não resolvido — informe o local ou cadastre o de-para.`);
                            }
                        }
                    }
                }
                else if (defaultId) {
                    stockLocationId = defaultId;
                    warnings.push('Sem depósito na nota — usando local padrão.');
                }
                else {
                    blockers.push('Informe o local de estoque (sem depósito na nota e sem padrão org).');
                }
            }
            lines.push({
                itemId: item.id,
                lineNumber: item.lineNumber,
                productId: item.productId,
                variantId: item.variantId ?? null,
                description: item.description,
                quantity: item.quantity,
                unit: item.unit?.trim() || null,
                baseQuantity: item.baseQuantity ?? null,
                baseUnit: item.baseUnit ?? null,
                conversionFactor: item.conversionFactor ?? null,
                unitPrice: item.unitPrice,
                lotText,
                warehouseText,
                lotRequired,
                lotId,
                stockLocationId,
                warehouseUnmapped,
                cfop: item.cfop?.trim() || null,
                stockControlEnabled: control.stockControlEnabled,
                stockControlByVolume: control.stockControlByVolume,
                productDescription: product?.description?.trim() || null,
                warnings,
                blockers,
            });
        }
        const canPost = invoice.status === 'draft' &&
            invoice.generatesStockEntry !== false &&
            lines.length > 0 &&
            lines.every((l) => l.blockers.length === 0);
        return { invoice, lines, canPost };
    }
    /**
     * Authorize IN invoice and post stock entries (idempotent).
     * OUT / generatesStockEntry=false → authorize only, no movements.
     */
    async authorizeWithStockEntry(orgId, invoiceId, params) {
        const repo = await this.getInvoiceRepo(orgId);
        const invoice = await repo.getById(invoiceId);
        if (!invoice)
            throw new Error('Nota fiscal não encontrada.');
        if (invoice.status === 'authorized')
            return invoice;
        if (!canInvoiceTransition(invoice.status, 'authorized')) {
            throw new Error('Não é possível autorizar esta nota no status atual.');
        }
        if (invoice.direction === 'OUT' && params.generatesStockEntry) {
            throw new Error('Nota de saída nunca gera movimento de estoque.');
        }
        // Persist flag + optional lot text updates before posting.
        const itemCrud = await this.itemsCrud(orgId, invoiceId);
        const items = await itemCrud.getAll();
        if (params.lotTexts) {
            for (const item of items) {
                if (Object.prototype.hasOwnProperty.call(params.lotTexts, item.id)) {
                    const lot = params.lotTexts[item.id]?.trim() || null;
                    if ((item.lot ?? null) !== lot) {
                        await itemCrud.update(item.id, { ...item, lot });
                    }
                }
            }
        }
        await invoiceService.patchFromSystem(orgId, invoiceId, {
            generatesStockEntry: params.generatesStockEntry,
        }, 'invoiceStock.authorizeWithStockEntry');
        if (invoice.direction === 'IN' &&
            params.generatesStockEntry &&
            params.allocations.length > 0) {
            const freshItems = await itemCrud.getAll();
            const byId = new Map(freshItems.map((i) => [i.id, i]));
            const movementDate = toMovementDateIso(invoice.entryDate ?? invoice.issueDate);
            // 0206 §1: the trigger is per **item** CFOP, never the invoice header — a note can
            // legitimately mix third-party and own-stock lines. An item with no CFOP of its own is
            // treated as own stock even when the header has one — the conservative default that
            // matches criterion 1's non-regression spirit (never silently guess "third-party").
            const thirdPartyCfops = parseThirdPartyCfops(params.orgStockParams?.['thirdPartyEntryCfops']);
            const thirdPartyStockLocationId = typeof params.orgStockParams?.['thirdPartyStockLocationId'] === 'string'
                ? params.orgStockParams['thirdPartyStockLocationId'].trim() || null
                : null;
            // 0206 §1.2 — pre-scan every allocation before writing anything: a third-party item
            // without a configured destination refuses the *whole* authorize call, never a partial
            // write followed by a failure mid-loop.
            if (thirdPartyCfops.length > 0 && !thirdPartyStockLocationId) {
                const hasThirdPartyItem = params.allocations.some((alloc) => {
                    const item = byId.get(alloc.itemId);
                    return item != null && isThirdPartyCfop(item.cfop, thirdPartyCfops);
                });
                if (hasThirdPartyItem) {
                    throw new Error('Configure o local de estoque de terceiros antes de efetivar notas de industrialização.');
                }
            }
            const productRepo = await repositoryResolver.resolveWithContext(PRODUCT, {
                orgId,
            });
            const productById = new Map();
            const loadProductForStock = async (productId) => {
                const key = productId.trim();
                if (!key)
                    return null;
                if (productById.has(key))
                    return productById.get(key) ?? null;
                const row = (await productRepo.getById(key).catch(() => null)) ?? null;
                productById.set(key, row);
                return row;
            };
            for (const alloc of params.allocations) {
                const item = byId.get(alloc.itemId);
                if (!item)
                    throw new Error(`Item não encontrado: ${alloc.itemId}`);
                const lotId = alloc.lotId?.trim() || null;
                const qty = Number(item.baseQuantity ?? item.quantity) || 0;
                if (!(qty > 0))
                    continue;
                const isThirdParty = isThirdPartyCfop(item.cfop, thirdPartyCfops);
                let stockLocationId;
                let ownerPartyId = null;
                let unitCost;
                let totalCost;
                if (isThirdParty) {
                    // Forced destination (0206 §2.1) — any other location the conciliation chose would
                    // be refused with 422 by the 0203 guard anyway, so forcing + showing it in the
                    // dialog is more honest than letting the operator pick a location that can't stick.
                    stockLocationId = thirdPartyStockLocationId ?? undefined;
                    ownerPartyId = invoice.partyId;
                    unitCost = 0;
                    totalCost = 0;
                }
                else {
                    stockLocationId = alloc.stockLocationId?.trim();
                    totalCost = round2((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0));
                    unitCost = qty > 0 ? totalCost / qty : 0;
                }
                if (!stockLocationId) {
                    throw new Error(`Local obrigatório no item ${item.lineNumber}.`);
                }
                const product = item.productId?.trim()
                    ? await loadProductForStock(item.productId)
                    : null;
                const stockControl = resolveProductStockControl(product ?? {}, params.orgStockParams ?? null);
                if (!stockControl.values.stockControlEnabled) {
                    const label = product
                        ? formatEntityLabel('Product', product, 'code+description')
                        : item.description?.trim() || item.productId;
                    throw new Error(`Linha ${item.lineNumber} (${label}): produto não movimenta estoque.`);
                }
                await params.stock.postEntry({
                    movementDate,
                    productId: item.productId,
                    variantId: item.variantId ?? null,
                    lotId,
                    stockLocationId,
                    quantity: qty,
                    unitCost,
                    totalCost,
                    sourceType: SOURCE_TYPE,
                    sourceId: invoiceId,
                    idempotencyKey: `${invoiceId}:${item.id}:${item.variantId ?? item.productId}:${lotId ?? 'nolot'}`,
                    ownerPartyId,
                });
                const warehouseCode = alloc.warehouseCode?.trim();
                if (warehouseCode) {
                    await warehouseMapService.upsert(orgId, {
                        warehouseCode,
                        stockLocationId,
                    });
                }
            }
        }
        await invoiceService.patchFromSystem(orgId, invoiceId, { status: 'authorized' }, 'invoiceStock.authorizeWithStockEntry');
        const updated = await repo.getById(invoiceId);
        if (!updated)
            throw new Error('Nota não encontrada após autorizar.');
        rtLogService.info('[invoice-stock] authorized', {
            orgId,
            invoiceId,
            generatesStockEntry: params.generatesStockEntry,
        });
        return updated;
    }
    /**
     * Cancel authorized invoice: F1-conditional unpost of invoice entries, then status cancelled.
     */
    async cancelWithUnpost(orgId, invoiceId, stock) {
        const repo = await this.getInvoiceRepo(orgId);
        const invoice = await repo.getById(invoiceId);
        if (!invoice)
            throw new Error('Nota fiscal não encontrada.');
        if (invoice.status === 'cancelled')
            return invoice;
        if (!canInvoiceTransition(invoice.status, 'cancelled')) {
            throw new Error('Não é possível cancelar esta nota no status atual.');
        }
        if (invoice.status === 'authorized' && invoice.direction === 'IN') {
            // Divergence (and later quarantine) must reverse before entries — otherwise
            // on-hand pre-check fails or leftover adjustments remain (0131 §5).
            await this.reversePreEntrySources(stock, invoiceId);
            const open = await this._openEntryMovements(stock, invoiceId);
            for (const m of open) {
                await this._assertEntryStillOnHand(stock, m);
            }
            if (open.length > 0) {
                await stock.reverseUnpostMany(open.map((m) => m.id), `invoice-reverse:${invoiceId}`, 'Cancelamento da nota fiscal');
            }
            // Volumes leave the Σ (status consumed) so invariant holds after unpost (0122 / 0121).
            await stock.consumeHandlingUnitsBySource(SOURCE_TYPE, invoiceId);
        }
        // Clear marks before status write so a failed clear is retryable (cancelled short-circuits).
        if (invoice.direction === 'IN') {
            await invoiceReceivingService.clearReceivingMarks(orgId, invoiceId);
        }
        await invoiceService.patchFromSystem(orgId, invoiceId, { status: 'cancelled' }, 'invoiceStock.cancelWithUnpost');
        const updated = await repo.getById(invoiceId);
        if (!updated)
            throw new Error('Nota não encontrada após cancelar.');
        rtLogService.info('[invoice-stock] cancelled', { orgId, invoiceId });
        return updated;
    }
    async listOpenEntries(orgId, invoiceId, stock) {
        void orgId;
        return this._openEntryMovements(stock, invoiceId);
    }
    async _findOrCreateLotId(orgId, productId, variantId, lotNumber, supplierId, expiryDate, orgStockParams) {
        const lots = await lotService.list(orgId);
        const existing = lots.find((l) => l.productId === productId &&
            (l.lotNumber ?? '').trim() === lotNumber &&
            l.active !== false);
        if (existing)
            return existing.id;
        const product = (await repositoryResolver
            .resolveWithContext(PRODUCT, { orgId })
            .then((r) => r.getById(productId))) ?? undefined;
        const created = await lotService.create(orgId, {
            lotNumber,
            productId,
            variantId,
            supplierId,
            expiryDate,
            active: true,
        }, {
            orgId,
            orgParams: orgStockParams ? { stock: orgStockParams } : undefined,
            extra: { siblings: lots, product },
        });
        return created.id;
    }
    async _openEntryMovements(stock, invoiceId) {
        const movements = await stock.listBySource(SOURCE_TYPE, invoiceId);
        const alreadyReversed = new Set(movements.map((m) => m.reversedMovementId).filter((id) => !!id));
        return movements.filter((m) => !m.reversedMovementId &&
            !alreadyReversed.has(m.id) &&
            (m.type === 'entry' || (typeof m.quantity === 'number' && m.quantity > 0)));
    }
    async _assertEntryStillOnHand(stock, m) {
        const productId = m.productId?.trim();
        const stockLocationId = m.stockLocationId?.trim();
        const need = Math.abs(Number(m.quantity) || 0);
        if (!productId || !stockLocationId || !(need > 0)) {
            throw new Error('Movimento de entrada incompleto no razão — não é possível validar o saldo para cancelar.');
        }
        const onHand = await stock.getOnHand({
            productId,
            variantId: m.variantId ?? null,
            lotId: m.lotId ?? null,
            stockLocationId,
        });
        if (onHand.available + 1e-9 < need) {
            throw new Error('Parte desta entrada já foi consumida; corrija por ajuste antes de cancelar a nota.');
        }
        if (m.lotId && (onHand.lotQuantity ?? 0) + 1e-9 < need) {
            throw new Error('Parte desta entrada já foi consumida (lote); corrija por ajuste antes de cancelar a nota.');
        }
    }
}
/**
 * Movements that must reverse **before** invoice entries on cancel (0131 / 0034 §5).
 * F3 will append quarantine transfer source types here.
 */
InvoiceStockService.PRE_ENTRY_REVERSE_SOURCE_TYPES = [
    'invoice-divergence',
    'invoice-quarantine',
];
export const invoiceStockService = new InvoiceStockService();
export const INVOICE_STOCK_SOURCE_TYPE = SOURCE_TYPE;
//# sourceMappingURL=invoiceStockService.js.map